import { useState, useContext, useEffect, useMemo, useRef } from "react";

//Components
import { FormControl, Modal, TextField, Button } from "@material-ui/core";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import { KeyboardDatePicker } from "@material-ui/pickers";
import PhoneInput from "./PhoneInput";
import AddressInputAutoSuggest from "./AddressInputAutoSuggest";

//Contexts
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";

//Fetching
import { useLazyQuery } from "@apollo/client";
import { PERSON_AUTO_SUGGEST } from "../Queries/PersonAutoSuggest";

//Styling
import { useStyles } from "../Styles/styles";

//Utils
import {
  formatName,
  handleUuid,
  personAlertColor,
  missingLastName,
} from "../utils/utils";
import { MutationContext } from "../Contexts/MutationContext";
import { UserContext } from "../Contexts/UserContext";
import { shouldBeLocked } from "../utils/Roles";
import { PERSON_ACTIONS } from "../Actions/PersonActions";

import moment from "moment";

const PersonAutoSuggest = ({
  showNameModal,
  setShowNameModal,
  showEditModal,
  setShowEditModal,
  formControlStyle,
  modalStyle,
  modalInputStyle,
  paperStyle,
  pisIndex,
  person,
  pisId,
  pisUuid,
  incidentId,
  incidentIndex,
  business,
  inIncident = true,
  userRoles,
  setPausePolling = () => alert("Component is missing setPausePolling"),
  incidentPeople = [],
  personNameEditOnly = false,
  confirmPersonNameEdit = false,
  openEditModalOnNameClick = true,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(undefined, props?.canEdit, userRoles),
    [props?.canEdit, userRoles],
  );
  const filter = createFilterOptions();
  const classes = useStyles();
  const { incidentDispatch, personDispatch } = useContext(IncidentsContext);
  const { responseErrorHandler, user, onlineState } = useContext(UserContext);
  const {
    UPDATE_PIS,
    UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
    UPDATE_PERSON_IN_PIS,
  } = INCIDENT_ACTIONS;
  const { MERGE_PERSON_PERSONAL_ATTRIBUTE } = PERSON_ACTIONS;
  const {
    addOrUpdatePersonInIncident,
    throttledAddOrUpdatePersonInIncident,
    updatePerson,
    throttledUpdatePerson,
    updatePersonPhone,
  } = useContext(MutationContext);

  const [localPerson, setLocalPerson] = useState(person);
  const nameInputRef = useRef(null);
  const focusGuardRef = useRef(false);
  const [currentPerson, setCurrentPerson] = useState({
    lastName: person?.personalAttribute?.lastName,
    firstName: person?.personalAttribute?.firstName,
    middleName: person?.personalAttribute?.middleName,
    suffix: person?.personalAttribute?.suffix,
    organization: person?.personalAttribute?.organization || false,
    dob: person?.personalAttribute?.dob
      ? moment
          .utc(person?.personalAttribute?.dob, "YYYY-MM-DD HH:mm:ss UTC")
          .local()
      : "",
    phone: person?.personalAttribute?.phone,
    address: {
      addressName: person?.personalAttribute?.homeAddress?.name,
    },
  });

  useEffect(() => {
    setLocalPerson(person);
    setCurrentPerson({
      lastName: person?.personalAttribute?.lastName,
      firstName: person?.personalAttribute?.firstName,
      middleName: person?.personalAttribute?.middleName,
      suffix: person?.personalAttribute?.suffix,
      organization: person?.personalAttribute?.organization || false,
      dob: person?.personalAttribute?.dob
        ? moment
            .utc(person?.personalAttribute?.dob, "YYYY-MM-DD HH:mm:ss UTC")
            .local()
        : "",
      phone: person?.personalAttribute?.phone,
      address: {
        addressName: person?.personalAttribute?.homeAddress?.name,
      },
    });
  }, [person]);

  const [personAutoSuggest] = useLazyQuery(PERSON_AUTO_SUGGEST, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        setPersonOptions(data.personAutoSuggest);
      }
    },
  });

  const [addPersonModal, setAddPersonModal] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const [personOptions, setPersonOptions] = useState([]);
  const [newPerson, setNewPerson] = useState({
    lastName: "",
    firstName: "",
    middleName: "",
    suffix: "",
    organization: business,
    dob: null,
    phone: null,
    address: {
      addressName: "",
    },
  });

  const clearFocusGuard = () => {
    window.setTimeout(() => {
      focusGuardRef.current = false;
    }, 0);
  };

  const closeNameModal = () => {
    focusGuardRef.current = true;
    if (nameInputRef.current) {
      nameInputRef.current.blur();
    }
    setShowNameModal(false);
    clearFocusGuard();
  };

  const closeEditModal = () => {
    focusGuardRef.current = true;
    if (nameInputRef.current) {
      nameInputRef.current.blur();
    }
    setShowEditModal(false);
    clearFocusGuard();
  };

  const openPersonModal = () => {
    if (memoizedLocked || focusGuardRef.current) {
      return;
    }
    if (person?.id && person?.personalAttribute?.lastName) {
      if (!openEditModalOnNameClick) {
        return;
      }
      setShowEditModal(true);
    } else {
      setShowNameModal(true);
    }
  };

  useEffect(() => {
    setNewPerson(prevState => {
      return { ...prevState, organization: business };
    });
  }, [business]);

  useEffect(() => {
    setCurrentPerson(prevState => {
      return { ...prevState, organization: business };
    });
  }, [business]);

  useEffect(() => {
    if (searchValue && searchValue.length > 2 && onlineState) {
      personAutoSuggest({ variables: { input: searchValue, count: 20 } });
    } else {
      setPersonOptions([]);
    }
  }, [searchValue, personAutoSuggest, onlineState]);

  const handleAddMatchToIncident = match => {
    const input = {
      incidentId: incidentId,
      personId: match?.id || "",
      //not sure what to do with personUuid at the moment
      personIncidentSpecificId: pisId,
      originatorId: user?.id,
    };
    //check that the match.id isn't present in the incidentPeople array of people (use person.id and .some())
    const personAlreadyOnIncident = incidentPeople.some(
      person => person.id === match.id,
    );
    if (personAlreadyOnIncident) {
      alert("This person is already on the incident");
      setSearchValue("");
      return;
    }
    inIncident &&
      addOrUpdatePersonInIncident({
        variables: { input },
        context: {
          serializationKey: "incidentQueue",
        },
      });
  };

  const handleNewPersonPersonChange = event => {
    let name = event.target.name || null;
    let value = event.target.value;
    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`,
      );
      return null;
    }
    setNewPerson(prevState => {
      return { ...prevState, [name]: value };
    });
  };
  const handleCurrentPersonPersonChange = event => {
    let name = event.target.name || null;
    let value = event.target.value;
    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`,
      );
      return null;
    }
    setCurrentPerson(prevState => {
      return { ...prevState, [name]: value };
    });
  };

  const handleDateChange = (value, name, date) => {
    setNewPerson(prevState => {
      return { ...prevState, [name]: date };
    });
  };
  const handleCurrentPersonDateChange = (value, name, date) => {
    setCurrentPerson(prevState => {
      return { ...prevState, [name]: date };
    });
  };

  const handleCreatePerson = () => {
    setAddPersonModal(false);
    // let personUuid = handleUuid()
    const input = {
      originatorId: user?.id,
      personId: person?.id,
      firstName: newPerson?.firstName,
      lastName: newPerson?.lastName,
      middleName: newPerson?.middleName,
      suffix: newPerson?.suffix,
      organization: newPerson?.organization,
      dob:
        moment.isMoment(newPerson?.dob) && newPerson?.dob?.isValid()
          ? newPerson.dob.utc().format("YYYY-MM-DD HH:mm:ss UTC")
          : "",
      phone: newPerson.phone,
      homeAddressId: newPerson?.address?.id,
      incidentId: incidentId ? incidentId : null,
    };

    //change this to update the person
    const personForState = {
      id: person.id,
      personalAttribute: {
        id: person?.personalAttribute?.id,
        firstName: newPerson?.firstName,
        lastName: newPerson?.lastName,
        middleName: newPerson?.middleName,
        suffix: newPerson?.suffix,
        organization: newPerson?.organization,
        dob:
          moment.isMoment(newPerson?.dob) && newPerson?.dob?.isValid()
            ? newPerson.dob.utc().format("YYYY-MM-DD HH:mm:ss UTC")
            : "",
        phone: newPerson?.phone,
        homeAddress: {
          ...newPerson?.address,
          addressName: newPerson?.address?.name,
        },
      },
    };
    inIncident &&
      incidentDispatch({
        type: UPDATE_PIS,
        payload: {
          incidentId: incidentId,
          pisId: pisId,
          property: "person",
          value: personForState,
        },
      });
    throttledUpdatePerson({
      variables: { input },
      context: {
        serializationKey: "incidentQueue",
      },
    });
    inIncident &&
      throttledAddOrUpdatePersonInIncident({
        variables: {
          input: {
            originatorId: user?.id,
            incidentId: incidentId,
            personId: person.id,
            personIncidentSpecificId: pisId,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      }).then(res => {
        incidentDispatch({
          type: UPDATE_PERSON_IN_PIS,
          payload: {
            pisId: pisId,
            incidentId: incidentId,
            property: "id",
            value:
              res?.data?.addOrUpdatePersonInIncident?.personIncidentSpecific
                ?.person?.id,
          },
        });
      });
  };

  const nameFieldsForEdit = business
    ? ["lastName"]
    : ["lastName", "firstName", "middleName"];

  const normalizeNameValue = value =>
    typeof value === "string" ? value.trim() : value || "";

  const getEditedNameValues = () =>
    nameFieldsForEdit.reduce((values, field) => {
      values[field] = normalizeNameValue(currentPerson[field]);
      return values;
    }, {});

  const getChangedNameFields = editedNameValues =>
    nameFieldsForEdit.filter(
      field =>
        editedNameValues[field] !==
        normalizeNameValue(person?.personalAttribute?.[field]),
    );

  const confirmNameChanges = editedNameValues => {
    if (!confirmPersonNameEdit) {
      return true;
    }

    const changedNameFields = getChangedNameFields(editedNameValues);
    if (changedNameFields.length === 0) {
      return true;
    }

    const currentName = business
      ? normalizeNameValue(person?.personalAttribute?.lastName)
      : formatName(
          person?.personalAttribute?.firstName,
          person?.personalAttribute?.middleName,
          person?.personalAttribute?.lastName,
        );
    const editedName = business
      ? editedNameValues.lastName
      : formatName(
          editedNameValues.firstName,
          editedNameValues.middleName,
          editedNameValues.lastName,
        );

    return window.confirm(
      `Update this ${business ? "business" : "person"} name from "${
        currentName || "blank"
      }" to "${
        editedName || "blank"
      }"?\n\nThis will change references in historical data.`,
    );
  };

  const updateNameOnlyState = editedNameValues => {
    const changedNameFields = getChangedNameFields(editedNameValues);

    if (changedNameFields.length === 0) {
      return changedNameFields;
    }

    if (inIncident) {
      changedNameFields.forEach(field => {
        incidentDispatch({
          type: UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
          payload: {
            incidentId: incidentId,
            pisId: pisId,
            personId: person?.id,
            personUuid: person?.uuid,
            pisUuid: pisUuid,
            property: field,
            value: editedNameValues[field],
          },
        });
      });
    } else {
      personDispatch({
        type: MERGE_PERSON_PERSONAL_ATTRIBUTE,
        payload: {
          personId: person?.id ? person.id : person?.uuid,
          value: editedNameValues,
        },
      });
    }

    return changedNameFields;
  };

  const handleEditPerson = () => {
    const editedNameValues = getEditedNameValues();
    if (!confirmNameChanges(editedNameValues)) {
      return;
    }

    if (personNameEditOnly) {
      const changedNameFields = updateNameOnlyState(editedNameValues);
      if (changedNameFields.length === 0) {
        closeEditModal();
        return;
      }

      updatePerson({
        variables: {
          input: {
            originatorId: user?.id,
            personId: person?.id ? person.id : person?.uuid,
            incidentId: incidentId ? incidentId : null,
            ...editedNameValues,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
      closeEditModal();
      return;
    }

    const input = {
      originatorId: user?.id,
      personId: person.id,
      firstName: currentPerson.firstName,
      lastName: currentPerson.lastName,
      middleName: currentPerson.middleName,
      suffix: currentPerson.suffix,
      organization: currentPerson.organization,
      dob: currentPerson?.dob
        ? currentPerson.dob.utc().format("YYYY-MM-DD HH:mm:ss UTC")
        : "",
      phone: currentPerson.phone,
      homeAddressId: currentPerson.address.id,
      incidentId: incidentId ? incidentId : null,
    };

    //change this to update the person
    const personForState = {
      id: person.id,
      personalAttribute: {
        firstName: currentPerson.firstName,
        lastName: currentPerson.lastName,
        middleName: currentPerson.middleName,
        suffix: currentPerson.suffix,
        organization: currentPerson.organization,
        dob:
          moment.isMoment(newPerson?.dob) && currentPerson?.dob?.isValid()
            ? currentPerson.dob.utc().format("YYYY-MM-DD HH:mm:ss UTC")
            : "",
        phone: currentPerson.phone,
        homeAddress: {
          ...currentPerson.address,
          addressName: currentPerson.address.name,
        },
      },
    };
    inIncident
      ? incidentDispatch({
          type: UPDATE_PIS,
          payload: {
            incidentId: incidentId,
            pisId: pisId,
            property: "person",
            value: personForState,
          },
        })
      : personDispatch({
          type: MERGE_PERSON_PERSONAL_ATTRIBUTE,
          payload: {
            personId: person?.id,
            value: {
              firstName: currentPerson.firstName,
              lastName: currentPerson.lastName,
              middleName: currentPerson.middleName,
              suffix: currentPerson.suffix,
            },
          },
        });

    updatePerson({
      variables: { input },
      context: {
        serializationKey: "incidentQueue",
      },
    });
    inIncident &&
      addOrUpdatePersonInIncident({
        variables: {
          input: {
            originatorId: user?.id,
            incidentId: incidentId,
            personId: person.id,
            personIncidentSpecificId: pisId,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      }).then(res => {
        incidentDispatch({
          type: UPDATE_PERSON_IN_PIS,
          payload: {
            pisId: pisId,
            incidentId: incidentId,
            property: "id",
            value:
              res?.data?.addOrUpdatePersonInIncident?.personIncidentSpecific
                ?.person?.id,
          },
        });
      });
    closeEditModal();
  };

  const updateAddress = address => {
    let localAddress = { ...address };
    localAddress.addressName = address?.name;
    // const input = {
    //   personId: person.id,
    //   homeAddressId: address.id
    // }
    setNewPerson(prevState => {
      return { ...prevState, address: localAddress };
    });
    // updatePersonHomeAddress({ variables: { input } })
  };

  const updateCurrentPersonAddress = address => {
    let localAddress = { ...address };
    localAddress.addressName = address?.name;
    // const input = {
    //   personId: person.id,
    //   homeAddressId: address.id
    // }
    setCurrentPerson(prevState => {
      return { ...prevState, address: localAddress };
    });
    // updatePersonHomeAddress({ variables: { input } })
  };

  const sendPhoneUpdate = (phoneNumber, propertyName) => {
    setNewPerson(prevState => {
      return { ...prevState, [propertyName]: phoneNumber };
    });
    incidentDispatch({
      type: UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
      payload: {
        incidentId: incidentId,
        pisId: pisId,
        personId: person?.id,
        personUuid: person?.uuid,
        pisUuid: pisUuid,
        property: propertyName,
        value: phoneNumber,
      },
    });
    updatePersonPhone({
      variables: {
        input: {
          originatorId: user?.id,
          incidentId: incidentId ? incidentId : null,
          personId: person?.id,
          [propertyName]: phoneNumber,
        },
      },
    });
  };

  return (
    <>
      {!showNameModal ? (
        <FormControl className={formControlStyle}>
          <TextField
            data-cy={"data-cy-auto-suggest-person-name-textfield-" + pisIndex}
            // disabled={person?.id && person?.personalAttribute?.lastName ? true : false}
            inputProps={{
              "data-cy": "person-name-input-" + pisIndex,
              style: { color: personAlertColor(person) },
            }}
            InputLabelProps={{ shrink: true }}
            inputRef={nameInputRef}
            id={"auto-suggest-person-name-" + handleUuid()}
            label="Name"
            disabled={!props?.canEdit}
            value={
              person?.personalAttribute?.organization
                ? person?.personalAttribute?.lastName
                  ? person?.personalAttribute?.lastName
                  : ""
                : formatName(
                    person?.personalAttribute?.firstName,
                    person?.personalAttribute?.middleName,
                    person?.personalAttribute?.lastName,
                  )
            }
            onClick={openPersonModal}
            onFocus={openPersonModal}
            error={missingLastName(person)}
            helperText={missingLastName(person) ? "Last name is blank" : ""}
          />
        </FormControl>
      ) : (
        <Autocomplete
          value={localPerson}
          //Add a new person on blur if there's any text
          // let nameSplit = newValue.inputValue.split(' ')
          //   nameSplit[0] = nameSplit[0].replace(/,\s*$/, "");
          //   setNewPerson(prevState => {
          //     return { ...prevState, lastName: nameSplit[0]?.trim() || "", firstName: nameSplit[1]?.trim() || "", middleName: nameSplit[2]?.trim() || "" }
          //   })
          onBlur={closeNameModal}
          autoHighlight
          autoSelect
          disabled={memoizedLocked}
          onInputChange={(event, newValue) => setSearchValue(newValue)}
          onChange={(event, newValue) => {
            if (
              typeof newValue === "object" &&
              newValue &&
              newValue.action !== "Add"
            ) {
              closeNameModal();
              handleAddMatchToIncident(newValue);
            }
            if (newValue && newValue.action && newValue.action === "Add") {
              let nameSplit = newValue.inputValue.split(" ");
              nameSplit[0] = nameSplit[0].replace(/,\s*$/, "");
              setNewPerson(prevState => {
                if (business) {
                  return { ...prevState, lastName: newValue.inputValue || "" };
                } else {
                  return {
                    ...prevState,
                    lastName: nameSplit[0]?.trim() || "",
                    firstName: nameSplit[1]?.trim() || "",
                    middleName: nameSplit[2]?.trim() || "",
                  };
                }
              });
              setAddPersonModal(true);
            }
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);

            // Suggest the creation of a new value
            if (params.inputValue !== "") {
              filtered.push({
                inputValue: params.inputValue,
                value: `Add "${params.inputValue}"`,
                action: "Add",
              });
            }

            return filtered;
          }}
          selectOnFocus
          // clearOnBlur
          handleHomeEndKeys
          id="free-solo-with-text-demo"
          options={personOptions}
          getOptionLabel={option => {
            // Value selected with enter, right from the input
            if (typeof option === "string") {
              return option;
            }
            // Add "xxx" option created dynamically
            if (option.inputValue) {
              return option.inputValue;
            }
            // Option when starting with a person
            if (option.personalAttribute) {
              return formatName(
                option.personalAttribute.firstName,
                option.personalAttribute.middleName,
                option.personalAttribute.lastName,
              );
            }
            // Autosuggest option
            return option.value ? option.value : "";
          }}
          renderOption={option => option.value}
          style={{ width: 300 }}
          freeSolo
          renderInput={params => (
            // eslint-disable-next-line jsx-a11y/no-autofocus -- Focus the replacement input when name editing starts.
            <TextField autoFocus
              {...params}
              inputProps={{
                ...params.inputProps,
                "data-cy": "person-autosuggest-input-" + pisIndex,
              }}
              label={business ? "Business Name" : "Last, First Middle"}
              variant="outlined"
            />
          )}
        />
      )}

      <Modal
        // disableEnforceFocus
        open={addPersonModal}
        onClose={() => setAddPersonModal(false)}
        className={modalStyle}
      >
        <div className={paperStyle}>
          <h3>Create a {business ? "business" : "person"}</h3>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            <TextField
              id={"person-last-name-" + pisId}
              label={business ? "Business Name" : "Last Name"}
              value={newPerson.lastName || ""}
              name="lastName"
              onChange={handleNewPersonPersonChange}
              disabled={memoizedLocked}
              // className={modalInputStyle}
            />
            {!business && (
              <>
                <TextField
                  id={"person-first-name-" + pisId}
                  label="First Name"
                  value={newPerson.firstName || ""}
                  name="firstName"
                  onChange={handleNewPersonPersonChange}
                  disabled={memoizedLocked}
                  // className={modalInputStyle}
                />
                <TextField
                  id={"person-middle-name-" + pisId}
                  label="Middle Name"
                  value={newPerson.middleName || ""}
                  name="middleName"
                  onChange={handleNewPersonPersonChange}
                  disabled={memoizedLocked}
                  // className={modalInputStyle}
                />
                <TextField
                  id={"person-suffix-" + pisId}
                  label="Suffix"
                  value={newPerson.suffix || ""}
                  name="suffix"
                  onChange={handleNewPersonPersonChange}
                  disabled={memoizedLocked}
                  // className={modalInputStyle}
                />
                <FormControl
                  className={classes.formControl}
                  style={{ marginRight: "4vw" }}
                >
                  <KeyboardDatePicker
                    data-cy={"create-person-dob-picker-" + pisId}
                    inputProps={{
                      "data-cy": "create-person-dob-picker-input-" + pisId,
                    }}
                    clearable
                    label="DOB"
                    value={
                      newPerson.dob
                        ? moment
                            .utc(newPerson.dob, "YYYY-MM-DD HH:mm:ss UTC")
                            .local()
                        : null
                    }
                    onChange={(date, value) =>
                      handleDateChange(value, "dob", date)
                    }
                    format="MM/DD/YYYY"
                    style={{ width: "150px" }}
                    disabled={memoizedLocked}
                  />
                </FormControl>
              </>
            )}
            <PhoneInput
              setPhoneNumber={sendPhoneUpdate}
              phoneString={newPerson.phone || ""}
              pisId={pisId}
              personId={person?.id}
              modalClass={classes.modal}
              inputClass={classes.modalInput}
              paperClass={classes.paper}
              title={"Phone"}
              propertyName={"phone"}
              userRoles={userRoles}
              {...props}
            />
            <AddressInputAutoSuggest
              address={newPerson.address}
              id={person?.id || null}
              setAddress={updateAddress}
              updateType={"person"}
              title={"Address"}
              userRole={userRoles}
              pauseLocationPolling={() => setPausePolling(true)}
              resumeLocationPolling={() => setPausePolling(false)}
              {...props}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              style={{ marginRight: "1vw" }}
              onClick={!memoizedLocked ? () => handleCreatePerson() : null}
            >
              Create {business ? "Business" : "Person"}
            </Button>
            <Button
              onClick={
                !memoizedLocked
                  ? () => {
                      setNewPerson({
                        firstName: "",
                        middleName: "",
                        lastName: "",
                        suffix: "",
                      });
                      setAddPersonModal(false);
                    }
                  : null
              }
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        // disableEnforceFocus
        open={showEditModal}
        onClose={closeEditModal}
        className={modalStyle}
      >
        <div className={paperStyle}>
          <h3>
            {personNameEditOnly
              ? `Edit ${business ? "business" : "person"} name`
              : `Edit a ${business ? "business" : "person"}`}
          </h3>
          <TextField
            id={"person-last-name-" + pisId}
            label={business ? "Business Name" : "Last Name"}
            value={currentPerson.lastName || ""}
            name="lastName"
            onChange={handleCurrentPersonPersonChange}
            className={modalInputStyle}
            disabled={memoizedLocked}
          />
          {!business && (
            <>
              <TextField
                id={"person-first-name-" + pisId}
                label="First Name"
                value={currentPerson.firstName || ""}
                name="firstName"
                onChange={handleCurrentPersonPersonChange}
                className={modalInputStyle}
                disabled={memoizedLocked}
              />
              <TextField
                id={"person-middle-name-" + pisId}
                label="Middle Name"
                value={currentPerson.middleName || ""}
                name="middleName"
                onChange={handleCurrentPersonPersonChange}
                className={modalInputStyle}
                disabled={memoizedLocked}
              />
              {!personNameEditOnly && (
                <TextField
                  id={"person-suffix-" + pisId}
                  label="Suffix"
                  value={currentPerson.suffix || ""}
                  name="suffix"
                  onChange={handleCurrentPersonPersonChange}
                  className={modalInputStyle}
                  disabled={memoizedLocked}
                />
              )}
            </>
          )}
          {inIncident && !personNameEditOnly && (
            <>
              <FormControl
                className={classes.formControl}
                style={{ marginRight: "4vw" }}
              >
                <KeyboardDatePicker
                  data-cy={"create-person-dob-picker-" + pisId}
                  inputProps={{
                    "data-cy": "create-person-dob-picker-input-" + pisId,
                  }}
                  clearable
                  label="DOB"
                  value={
                    currentPerson.dob
                      ? moment
                          .utc(currentPerson.dob, "YYYY-MM-DD HH:mm:ss UTC")
                          .local()
                      : null
                  }
                  onChange={(date, value) =>
                    handleCurrentPersonDateChange(value, "dob", date)
                  }
                  format="MM/DD/YYYY"
                  style={{ width: "150px" }}
                  disabled={memoizedLocked}
                />
              </FormControl>
              <FormControl className={classes.formControl}>
                <PhoneInput
                  setPhoneNumber={sendPhoneUpdate}
                  phoneString={currentPerson.phone || ""}
                  pisId={pisId}
                  personId={person?.id}
                  modalClass={classes.modal}
                  inputClass={classes.modalInput}
                  paperClass={classes.paper}
                  title={"Phone"}
                  propertyName={"phone"}
                  userRoles={userRoles}
                  {...props}
                />
              </FormControl>
              <AddressInputAutoSuggest
                address={currentPerson.address}
                id={person?.id || null}
                setAddress={updateCurrentPersonAddress}
                updateType={"person"}
                title={"Address"}
                userRoles={userRoles}
                pauseLocationPolling={() => setPausePolling(true)}
                resumeLocationPolling={() => setPausePolling(false)}
                {...props}
              />
            </>
          )}
          <Button onClick={!memoizedLocked ? () => handleEditPerson() : null}>
            Update {business ? "Business" : "Person"}
            {personNameEditOnly ? " Name" : ""}
          </Button>
          <Button onClick={closeEditModal}>Cancel</Button>
        </div>
      </Modal>
    </>
  );
};

export default PersonAutoSuggest;
